import { Controller } from "@hotwired/stimulus"

// Persists the scrollTop of the controller's element across Turbo navigations,
// keyed by `key` value so multiple instances on a page do not collide.
//
// If the element lives inside a [data-toggle-open-open-value] section, restore
// is deferred until the section is open AND its max-height transition has
// finished — otherwise the browser clamps scrollTop to 0 on a max-h-0 /
// overflow-hidden element and the saved position is lost.
export default class extends Controller {
  static values = { key: String }

  connect() {
    if (!this.keyValue) return

    this.collapsibleSection = this.element.closest("[data-toggle-open-open-value]")
    this.element.addEventListener("scroll", this.#save, { passive: true })

    if (this.#isOpen) this.#restore()

    if (this.collapsibleSection) {
      this.observer = new MutationObserver(this.#onSectionToggled)
      this.observer.observe(this.collapsibleSection, {
        attributeFilter: ["data-toggle-open-open-value"],
        attributes: true,
      })
    }
  }

  disconnect() {
    this.element.removeEventListener("scroll", this.#save)
    this.element.removeEventListener("transitionend", this.#onTransitionEnd)
    clearTimeout(this.fallbackTimeout)
    this.observer?.disconnect()
  }

  get #isOpen() {
    return !this.collapsibleSection || this.collapsibleSection.dataset.toggleOpenOpenValue === "true"
  }

  #onSectionToggled = () => {
    this.element.removeEventListener("transitionend", this.#onTransitionEnd)
    clearTimeout(this.fallbackTimeout)
    if (!this.#isOpen) return

    // Wait for max-height to finish transitioning before restoring; the
    // timeout is a fallback for reduced-motion users (transitionend may not
    // fire) and for interrupted transitions.
    this.element.addEventListener("transitionend", this.#onTransitionEnd)
    this.fallbackTimeout = setTimeout(this.#finishRestore, 350)
  }

  #onTransitionEnd = (event) => {
    // transitionend bubbles — only react to our own max-height transition.
    if (event.target !== this.element || event.propertyName !== "max-height") return
    this.#finishRestore()
  }

  #finishRestore = () => {
    this.element.removeEventListener("transitionend", this.#onTransitionEnd)
    clearTimeout(this.fallbackTimeout)
    if (this.#isOpen) this.#restore()
  }

  #restore = () => {
    const stored = this.#read()
    if (stored !== null) this.element.scrollTop = stored
  }

  #save = () => {
    if (!this.#isOpen) return
    try {
      sessionStorage.setItem(this.#storageKey, this.element.scrollTop)
    } catch {
      // sessionStorage may be disabled (private mode) or full
    }
  }

  #read = () => {
    let raw
    try {
      raw = sessionStorage.getItem(this.#storageKey)
    } catch {
      return null
    }
    if (raw === null) return null
    const value = parseInt(raw, 10)
    return Number.isFinite(value) ? value : null
  }

  get #storageKey() {
    return `preserve-scroll:${this.keyValue}`
  }
}
